import { createHashRouter, useNavigate, useParams } from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import { useEffect } from "react";
import { ConfigProvider } from "antd";

import App from "App";
import HomePage from "pages/home";
import ListPage from "pages/list";
import EditPage from "pages/edit";
import SettingsPage from "pages/settings";
import DashboardPage from "pages/dashboard";
import NotFoundInPdf, { NOT_FOUND_IN_PDF } from "pages/dashboard/not-found-in-pdf";
import AiMapPage from "pages/ai-map";
import InsightPage from "pages/insight";
import ReCalCulPage from "pages/re-calcul";
import ControlPage from "pages/control";
import CSTBPage from "pages/cstb";
import LoginPage from "pages/login";
import SignUpPage from "pages/signup";
import ConfirmationSignUpPage from "pages/confirmation-signup";
import ForgotPasswordPage from "pages/forgot-password";
import UploadPage from "pages/upload";
import NeighborsMapPage from "pages/neighbors-map";
import IframeMondpePage from "pages/iframe-mondpe";
import IframeFiabilitePage from "pages/iframe-fiabilite";

import PAGES from "./constants";
import { ADDRESS_PRIMARY_COLOR } from "constants/address";
import { getLandingLocale } from "constants/landing";
import StripePaymentPage from "pages/stripe-payment";
import AccountPage from "pages/account";
import NotFound from "./not-found";
import { getUserToken } from "services/storages/userStorage";
import { setLastAccessPage } from "services/storages/expiredStorage";

function Redirect({ to }: { to: string }) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function PrivateRoute({ children }: { children: JSX.Element }) {
  const userToken = getUserToken();
  if (!userToken) {
    setLastAccessPage(window.location.hash?.slice(1));
    return <Redirect to={PAGES.login} />;
  }
  return children;
}

const LandingJSON = getLandingLocale();

// Upload flow redirects to /dashboard/NOT_FOUND_IN_PDF when the PDF has no ref ADEME
const DashboardRoute = () => {
  const { id } = useParams();
  return id === NOT_FOUND_IN_PDF ? <NotFoundInPdf /> : <DashboardPage />;
};

export const router = createHashRouter([
  {
    path: PAGES.home,
    element: (
      <QueryParamProvider adapter={ReactRouter6Adapter}>
        <App />
      </QueryParamProvider>
    ),

    // loader: rootLoader,
    children: [
      {
        path: PAGES.home,
        element: (
          <HomePage
            landingLocale={LandingJSON.top}
            bannerTwo={LandingJSON.banner_2.all}
          />
        ),
      },
      {
        path: PAGES.acheter,
        element: (
          <HomePage
            isPersonaePage
            landingLocale={LandingJSON.top}
            personaeLocale={LandingJSON.personae.acheter}
            bannerTwo={LandingJSON.banner_2.acheter}
          />
        ),
      },
      {
        path: PAGES.vendre,
        element: (
          <HomePage
            isPersonaePage
            landingLocale={LandingJSON.top}
            personaeLocale={LandingJSON.personae.vendre}
            bannerTwo={LandingJSON.banner_2.vendre}
          />
        ),
      },
      {
        path: PAGES.renover,
        element: (
          <HomePage
            isPersonaePage
            landingLocale={LandingJSON.top}
            personaeLocale={LandingJSON.personae.renover}
            bannerTwo={LandingJSON.banner_2.renover}
          />
        ),
      },
      {
        path: PAGES.list,
        element: <ListPage />,
      },
      {
        path: PAGES.settings,
        element: <SettingsPage />,
      },
      {
        path: PAGES.dashboard,
        element: <DashboardRoute />,
      },
      {
        path: PAGES.insight,
        element: <InsightPage />,
      },
      {
        path: PAGES.reCalculate,
        element: <ReCalCulPage />,
      },
      {
        path: PAGES.controls,
        element: <ControlPage />,
      },
      {
        path: PAGES.cstb,
        element: <CSTBPage />,
      },
      {
        path: PAGES.login,
        element: <LoginPage />,
      },
      {
        path: PAGES.signup,
        element: <SignUpPage />,
      },
      {
        path: PAGES.confirmationSignup,
        element: <ConfirmationSignUpPage />,
      },
      {
        path: PAGES.forgotPassword,
        element: <ForgotPasswordPage />,
      },
      {
        path: PAGES.stripe,
        element: <StripePaymentPage />,
      },
      {
        path: PAGES.paymentComplete,
        element: <StripePaymentPage />,
      },
      {
        path: PAGES.account,
        element: (
          <PrivateRoute>
            <AccountPage />
          </PrivateRoute>
        ),
      },
      {
        path: PAGES.address,
        element: (
          // Address page only: revamp primary color (app-wide theme left unchanged).
          // hashed: true is required - the root theme is unhashed, so an unhashed nested
          // theme would emit unscoped global styles and not override the app-wide ones.
          <ConfigProvider theme={{ hashed: true, token: { colorPrimary: ADDRESS_PRIMARY_COLOR } }}>
            <AiMapPage defaultSearchMode="address" />
          </ConfigProvider>
        ),
      },
      {
        path: PAGES.upload,
        element: <UploadPage />,
      },
    ],
  },
  {
    path: PAGES.edit,
    element: <EditPage />,
    // loader: rootLoader,
  },
  {
    path: PAGES.neighborsMap,
    element: <NeighborsMapPage />,
  },
  {
    path: PAGES.iframeMondpe,
    element: <IframeMondpePage />,
  },
  {
    path: PAGES.iframeFiabilite,
    element: <IframeFiabilitePage />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
]);
